// The cockpit's frame: a left instrument rail, the HUD strip on top, dense
// panes below.
//
// Every structural choice here is the opposite of the Studio's, on
// purpose: navigation is vertical not horizontal, lettering is condensed
// and monospaced not serif, the surface is instrument black not paper, and
// four live readings are always on screen rather than one account line.
// The two UIs are different tools for different jobs, and they should not
// look like the same product wearing two colour schemes.
import type { ReactNode } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useAdminStatus } from "../../lib/adminStatus";
import { RequireAdmin } from "../Guards";
import AdminHud from "./AdminHud";
import { useTheme } from "./useTheme";

export default function AdminShell() {
  const { user, signOut } = useAuth();
  useTheme("admin");
  // Only an admin's session can read these endpoints; a non-admin is
  // redirected by RequireAdmin below and must not fire the requests in the
  // meantime (a 401 there would sign them out on the way to /login).
  const status = useAdminStatus(user?.role === "ADMIN");

  return (
    <div className="a-app">
      <RequireAdmin>
        <nav className="a-rail">
          <div className="a-rail__brand">
            <Link className="a-rail__word" to="/admin">
              GPU·FAN
            </Link>
            <span className="a-rail__sub">Mission Control</span>
          </div>

          <span className="a-rail__sect">Mission</span>
          <RailLink to="/admin" end>
            Overview
          </RailLink>
          <RailLink to="/admin/tasks">Tasks</RailLink>

          <span className="a-rail__sect">Capacity</span>
          <RailLink to="/admin/compute">GPU fleet</RailLink>
          <RailLink to="/admin/policy">Policy</RailLink>

          <span className="a-rail__sect">Control</span>
          <RailLink to="/admin/killswitch">KillSwitch</RailLink>
          <RailLink to="/admin/users">Users</RailLink>
          <RailLink to="/admin/aliases">Aliases</RailLink>
          <RailLink to="/admin/prices">Prices</RailLink>

          <div className="a-rail__foot">
            <span className="a-rail__who">{user?.email}</span>
            <Link className="a-rail__act" to="/">
              ← Studio
            </Link>
            <button className="a-rail__act" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </nav>
        <div className="a-col">
          <AdminHud status={status} />
          <main className="a-main">
            {/* The HUD's reading is the whole cockpit's shared fact; a page
                that repeats it reads the same poll instead of starting a
                second one (views/admin/AdminOverview.tsx). */}
            <Outlet context={status} />
          </main>
        </div>
      </RequireAdmin>
    </div>
  );
}

function RailLink({ to, end, children }: { to: string; end?: boolean; children: ReactNode }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? "a-rail__link a-rail__link--on" : "a-rail__link")}>
      {children}
    </NavLink>
  );
}
